import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FiArrowLeft, FiMaximize2, FiMinimize2, FiUsers, FiSearch, FiMoreVertical, FiZap, FiBriefcase, FiInfo, FiBookmark } from "react-icons/fi";
import MessageList from "./MessageList";
import MessageInput from "./MessageInput";
import AIAssistant from "./AIAssistant";
import ChatSearchBar from "./ChatSearchBar";
import BusinessContextBadge from "./BusinessContextBadge";
import ClientContextPanel from "./ClientContextPanel";
import GroupInfoPanel from "./GroupInfoPanel";
import {
  markMessagesReadThunk,
  clearConversationUnread,
  setWidgetState,
  setActiveConversation,
} from "../../../Slices/chatSlice";
import { openTab } from "../../../Slices/tabSlice";
import { getInitials, getAvatarColor, getDisplayName } from "./avatarUtils";
import { getStatusDotClass, getStatusTextClass, getStatusLabel } from "./presenceUtils";
import socket from "../Sales1/socket";
import "./chatWidget.css";

const BOT_ID = 'ai-assistant-bot';

const ChatWindow = ({ mode = "fullscreen", onBack, onExpand, onMinimize }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showAI, setShowAI] = useState(false);
  const [showContext, setShowContext] = useState(false);
  const [showGroupInfo, setShowGroupInfo] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const activeConversationId = useSelector((state) => state.chat.activeConversationId);
  const conversation = useSelector((state) =>
    state.chat.conversations.find((c) => c.id === activeConversationId)
  );
  const loginUser = useSelector((state) => state?.loginUserInfo?.loginUserDetails);
  const allUsers = useSelector((state) => state?.loginUserInfo?.allUsers);
  const typingUsers = useSelector(
    (state) => state.chat.typingUsers[activeConversationId] || {}
  );
  const onlineUsers = useSelector((state) => state.chat.onlineUsers);

  // Bot conversation detection
  const isBotConversation = conversation?.category === 'ai-bot' ||
    conversation?.participants?.some((p) => p.userId === BOT_ID);

  // Self-chat detection
  const isSelfChat = conversation?.category === 'self';

  // Linked entity detection
  const linkedEntity = conversation?.linkedEntity;
  const hasLinkedEntity = linkedEntity?.entityType && linkedEntity?.entityId;

  // Join/leave socket room
  useEffect(() => {
    if (!activeConversationId) return;
    socket.emit("chat:join", { conversationId: activeConversationId });
    if (loginUser?.id) {
      dispatch(markMessagesReadThunk({ conversationId: activeConversationId, userId: loginUser.id }));
      dispatch(clearConversationUnread(activeConversationId));
    }
    return () => {
      socket.emit("chat:leave", { conversationId: activeConversationId });
    };
  }, [activeConversationId, loginUser?.id, dispatch]);

  const displayName = useMemo(() => {
    return getDisplayName(conversation, loginUser?.id, allUsers);
  }, [conversation, loginUser?.id, allUsers]);

  const typingText = useMemo(() => {
    const typers = Object.values(typingUsers);
    if (typers.length === 0) return "";
    if (typers.length === 1) return `${typers[0].userName} is typing...`;
    if (typers.length === 2) return `${typers[0].userName} and ${typers[1].userName} are typing...`;
    return "Several people are typing...";
  }, [typingUsers]);

  const otherUserId =
    conversation?.type === "direct"
      ? conversation?.participants?.find((p) => p.userId !== loginUser?.id)?.userId
      : null;
  const otherPresence = otherUserId ? onlineUsers[otherUserId] : null;
  const otherStatus = otherPresence?.status || "offline";
  const isOtherOnline = otherStatus === "online";
  const otherUser = otherUserId ? allUsers?.find((u) => u.id === otherUserId) : null;

  // Fullscreen actions
  const handleFullscreenMinimize = () => {
    dispatch(setWidgetState("open"));
    navigate(-1);
  };

  if (!activeConversationId || !conversation) {
    return (
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "var(--chat-bg-secondary)",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <FiUsers style={{ fontSize: 48, color: "var(--chat-text-muted)" }} />
        <span style={{ color: "var(--chat-text-muted)", fontSize: 14 }}>
          Select a conversation to start chatting
        </span>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Chat Header */}
      <div className="chat-header">
        {mode === "widget" && onBack && (
          <button className="chat-header__back" onClick={onBack} title="Back">
            <FiArrowLeft size={18} />
          </button>
        )}

        {isSelfChat ? (
          <div className="chat-avatar chat-avatar--md chat-avatar--self">
            <FiBookmark size={18} />
          </div>
        ) : isBotConversation ? (
          <div className="chat-avatar chat-avatar--md chat-avatar--bot">
            <FiZap size={18} />
          </div>
        ) : conversation.type === "group" ? (
          <div
            className="chat-avatar chat-avatar--md"
            style={{ background: "var(--chat-primary)" }}
          >
            <FiUsers size={18} />
          </div>
        ) : (
          <div
            className="chat-avatar chat-avatar--md"
            style={{ background: getAvatarColor(otherUserId) }}
          >
            {getInitials(otherUser?.firstname || displayName?.split(" ")[0], otherUser?.lastname || displayName?.split(" ")[1])}
            <span
              className={`chat-avatar__dot ${getStatusDotClass(otherStatus)}`}
              title={getStatusLabel(otherStatus, otherPresence?.customStatus, otherPresence?.lastSeen)}
            />
          </div>
        )}

        <div className="chat-header__info">
          <div className="chat-header__name" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {isSelfChat ? "Notes to Self" : isBotConversation ? "AI Assistant" : displayName}
            {hasLinkedEntity && (
              <BusinessContextBadge
                linkedEntity={linkedEntity}
                onClick={() => setShowContext(!showContext)}
              />
            )}
          </div>
          {typingText ? (
            <div className="chat-header__status chat-header__status--typing">{typingText}</div>
          ) : isSelfChat ? (
            <div className="chat-header__status chat-header__status--online">Always online</div>
          ) : isBotConversation ? (
            <div className="chat-header__status chat-header__status--online">Always online</div>
          ) : conversation.type === "direct" ? (
            <div className={`chat-header__status ${getStatusTextClass(otherStatus)}`}>
              {getStatusLabel(otherStatus, otherPresence?.customStatus, otherPresence?.lastSeen)}
            </div>
          ) : (
            <div
              className="chat-header__status"
              style={{ cursor: "pointer" }}
              onClick={() => { setShowGroupInfo(!showGroupInfo); setShowAI(false); setShowContext(false); }}
            >
              {conversation.participants?.length || 0} members
            </div>
          )}
        </div>

        <div className="chat-header__actions">
          {/* Group info button (only for group conversations) */}
          {conversation.type === "group" && !isBotConversation && (
            <button
              onClick={() => { setShowGroupInfo(!showGroupInfo); setShowAI(false); setShowContext(false); }}
              title="Group Info"
              type="button"
              className={`chat-header__action-btn ${showGroupInfo ? "chat-header__action-btn--active" : ""}`}
            >
              <FiUsers size={16} />
            </button>
          )}
          {/* Context panel button (only for linked entity conversations) */}
          {hasLinkedEntity && (
            <button
              onClick={() => { setShowContext(!showContext); setShowAI(false); }}
              title="Entity Context"
              type="button"
              className={`chat-header__action-btn ${showContext ? "chat-header__action-btn--active" : ""}`}
            >
              <FiBriefcase size={16} />
            </button>
          )}
          {/* Find in chat */}
          <button
            onClick={() => setShowSearch(!showSearch)}
            title="Find in chat (Ctrl+F)"
            type="button"
            className={`chat-header__action-btn ${showSearch ? "chat-header__action-btn--active" : ""}`}
          >
            <FiSearch size={16} />
          </button>
          {/* AI Assistant button */}
          <button
            onClick={() => { setShowAI(!showAI); setShowContext(false); }}
            title="AI Assistant"
            type="button"
            className={`chat-header__action-btn ${showAI ? "chat-header__action-btn--active" : ""}`}
          >
            <FiZap size={16} />
          </button>
          {mode === "widget" && onExpand && (
            <button
              onClick={onExpand}
              title="Open in tab"
              type="button"
              className="chat-header__action-btn"
            >
              <FiMaximize2 size={16} />
            </button>
          )}
          {mode === "widget" && onMinimize && (
            <button
              onClick={onMinimize}
              title="Minimize"
              type="button"
              className="chat-header__action-btn"
            >
              <FiMinimize2 size={16} />
            </button>
          )}
          {mode === "fullscreen" && (
            <button
              onClick={handleFullscreenMinimize}
              title="Minimize to widget"
              type="button"
              className="chat-header__action-btn"
            >
              <FiMinimize2 size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Find in chat bar */}
      {showSearch && (
        <ChatSearchBar
          conversationId={activeConversationId}
          onClose={() => setShowSearch(false)}
        />
      )}

      {/* Main content area */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden", position: "relative" }}>
        {/* Messages column */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
          <MessageList conversationId={activeConversationId} />
          <MessageInput conversationId={activeConversationId} isBotConversation={isBotConversation} />
        </div>

        {/* AI Assistant panel */}
        <AIAssistant
          open={showAI}
          onClose={() => setShowAI(false)}
          conversationId={activeConversationId}
          onInsertText={(text) => {
            window.dispatchEvent(new CustomEvent("chat:ai-insert", { detail: { text } }));
          }}
        />

        {/* Client/Entity Context panel */}
        <ClientContextPanel
          open={showContext}
          onClose={() => setShowContext(false)}
          entityType={linkedEntity?.entityType}
          entityId={linkedEntity?.entityId}
        />

        {/* Group Info panel */}
        <GroupInfoPanel
          open={showGroupInfo}
          onClose={() => setShowGroupInfo(false)}
          conversation={conversation}
        />
      </div>
    </div>
  );
};

export default ChatWindow;
